import { PieceJointeBar, type Tiers } from '@mairistem/comptabilite';
import { Fiche } from '@mairistem/fiche';
import { Fichiers, SemaphoreModal } from '@mairistem/finances-utils';
import type { ErrorField } from '@mairistem/liste';
import { isEmpty, isNumber } from 'lodash';
import React from 'react';
import TiersFichePanelAdressesContacts from '../FichePanel/TiersFichePanelAdressesContacts';
import TiersFichePanelIdentification from '../FichePanel/TiersFichePanelIdentification/TiersFichePanelIdentification';
import TiersFichePanelInformationsBancaires from '../FichePanel/TiersFichePanelInformationsBancaires';
import TiersFichePanelParametresGestion from '../FichePanel/TiersFichePanelParametresGestion/TiersFichePanelParametresGestion';
import TiersFichePanelParametresGestionSecondaire from '../FichePanel/TiersFichePanelParametresGestionSecondaire';
import TiersListeContainer from '../TiersListe/TiersListeContainer';

const getHeader = (
	identifiant: number | string,
	nom: string,
	codeInterne,
): string => {
	let header = `${nom} - ${codeInterne}`;

	if (isEmpty(nom) && codeInterne === 0) {
		if (isNumber(identifiant) && identifiant > 0) {
			header = '';
		} else {
			header = 'Nouveau tiers';
		}
	} else if (isEmpty(nom) && codeInterne > 0) {
		if (isNumber(identifiant) && identifiant > 0) {
			header = codeInterne;
		} else {
			header = `Nouveau tiers - ${codeInterne}`;
		}
	} else if (!isEmpty(nom) && codeInterne === 0) {
		header = nom;
	}

	return header;
};

interface TiersFicheConsumerProps {
	tiers?: Tiers;
	onChange?: (changes: Partial<Tiers>) => void;
	errors?: ErrorField[];
	isModalSemaphoreVisible: boolean;
	handleChangeModalSemaphoreVisible: (visible: boolean) => void;
}

const TiersFicheConsumer = ({
	tiers,
	onChange,
	errors,
	isModalSemaphoreVisible,
	handleChangeModalSemaphoreVisible,
	...rest
}: TiersFicheConsumerProps) => {
	const {
		identifiant,
		nom = '',
		codeInterne = 0,
		debiteurOuFournisseur,
		categoriePes,
		natureJuridiquePes,
		identifiantCivilite,
		complementNom,
		prenom,
		natureIdentification,
		identification,
		reference,
		identifiantCategorie,
		dateNaissance,
		dateValidite,
		delaiPaiement,
		renseignementsDivers,
		engagementObligatoire,
		generique,
		seuilAlerte,
		seuilBlocage,
		asapDematerialise,
		adresses = [],
		deletedDocuments,
	} = tiers || {};

	const handleChangePj = React.useCallback(
		(changes) => {
			onChange({
				identifiant,
				...changes,
			});
		},
		[identifiant, onChange],
	);

	const handleDeletePj = React.useCallback(
		(identifiantPj: number) => {
			onChange?.({
				identifiant,
				deletedDocuments: [...(deletedDocuments || []), identifiantPj],
			});
		},
		[identifiant, onChange, deletedDocuments],
	);

	const getPjSideBar = React.useMemo(
		() => (
			<PieceJointeBar
				identifiantFichierLie={identifiant}
				nomFichierLie={Fichiers.TIERS}
				onChange={handleChangePj}
				onDeletePj={handleDeletePj}
			/>
		),
		[handleChangePj, handleDeletePj, identifiant],
	);

	const sidebars = React.useMemo(
		() => ({
			left: <TiersListeContainer />,
			right: {
				content: getPjSideBar,
				icon: 'attach',
				alwaysPinned: true,
				width: '15vw',
			},
		}),
		[getPjSideBar],
	);

	const identificationPanel = React.useMemo(
		() => (
			<Fiche.Panel key="idx" id="idx" title="IDENTIFICATION">
				<TiersFichePanelIdentification
					onChange={onChange}
					identifiant={identifiant}
					debiteurOuFournisseur={debiteurOuFournisseur}
					categoriePes={categoriePes}
					natureJuridiquePes={natureJuridiquePes}
					identifiantCivilite={identifiantCivilite}
					nom={nom}
					complementNom={complementNom}
					prenom={prenom}
					natureIdentification={natureIdentification}
					identification={identification}
					reference={reference}
					codeInterne={codeInterne}
					identifiantCategorie={identifiantCategorie}
					dateNaissance={dateNaissance}
					errors={errors}
				/>
			</Fiche.Panel>
		),
		[
			categoriePes,
			identifiantCivilite,
			codeInterne,
			complementNom,
			dateNaissance,
			debiteurOuFournisseur,
			onChange,
			identifiant,
			identifiantCategorie,
			identification,
			natureIdentification,
			natureJuridiquePes,
			nom,
			prenom,
			reference,
			errors,
		],
	);

	const informationsBancairesPanel = React.useMemo(
		() => (
			<Fiche.Panel
				key="InformationsBancaires"
				id="InformationsBancaires"
				title="INFORMATIONS BANCAIRES"
			>
				<TiersFichePanelInformationsBancaires
					onChange={onChange}
					tiers={tiers}
				/>
			</Fiche.Panel>
		),
		[onChange, tiers],
	);

	const adressesContactsPanel = (
		<Fiche.Panel
			key="AdressesContact"
			id="AdressesContact"
			title="ADRESSES/ CONTACTS"
		>
			<TiersFichePanelAdressesContacts
				onChange={onChange}
				contacts={tiers?.contacts}
				identifiant={tiers?.identifiant}
				adresses={adresses}
				deletedAdresses={tiers?.deletedAdresses}
				deletedContacts={tiers?.deletedContacts}
			/>
		</Fiche.Panel>
	);

	const parametresGestionPanel = React.useMemo(
		() => (
			<Fiche.Panel
				key="ParametresGestion"
				id="ParametresGestion"
				title="PARAMETRES DE GESTION"
			>
				<TiersFichePanelParametresGestion
					identifiant={identifiant}
					onChange={onChange}
					dateValidite={dateValidite}
					delaiPaiement={delaiPaiement}
					engagementObligatoire={engagementObligatoire}
					generique={generique}
					asapDematerialise={asapDematerialise}
					debiteurOuFournisseur={debiteurOuFournisseur}
				/>
				<TiersFichePanelParametresGestionSecondaire
					identifiant={identifiant}
					onChange={onChange}
					renseignementsDivers={renseignementsDivers}
					seuilAlerte={seuilAlerte}
					seuilBlocage={seuilBlocage}
				/>
			</Fiche.Panel>
		),
		[
			onChange,
			asapDematerialise,
			dateValidite,
			debiteurOuFournisseur,
			delaiPaiement,
			engagementObligatoire,
			generique,
			identifiant,
			renseignementsDivers,
			seuilAlerte,
			seuilBlocage,
		],
	);

	const header = getHeader(identifiant, nom, codeInterne);

	return (
		<span className="ficheWithSideBar">
			<Fiche
				key="TiersFiche"
				type="tiers"
				header={header}
				sidebars={sidebars}
				errors={errors}
				{...rest}
			>
				{identificationPanel}
				{adressesContactsPanel}
				{informationsBancairesPanel}
				{parametresGestionPanel}
				{isModalSemaphoreVisible && (
					<SemaphoreModal onChangeVisible={handleChangeModalSemaphoreVisible} />
				)}
			</Fiche>
		</span>
	);
};

export default TiersFicheConsumer;
